import {z} from "zod/v4-mini"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {createCurveGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {CurveGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import {GeometryAttributeNode, curveGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {CurveGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {AttributeRef} from "#template/geometry-processing/geometry-graph"
import {Operators} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

// Single-variant operator (curve-only): parameter type is concrete, no per-dim genericity.
export type DiffOperatorParameters = {
    name: string
    independentAttribute: CurveGeometryAttributeLikeNode | null
    dependentAttribute: CurveGeometryAttributeLikeNode | null
}

export const diffOperatorParametersSchema = z.object({
    name: z.string(),
    independentAttribute: z.nullable(curveGeometryAttributeLike),
    dependentAttribute: z.nullable(curveGeometryAttributeLike),
})

function applyDiffOperator(independentAttribute: CurveGeometryAttributeData, dependentAttribute: CurveGeometryAttributeData): CurveGeometryAttributeData {
    const independentAttrRefData = independentAttribute.ref
    const dependentAttrRefData = dependentAttribute.ref

    if (independentAttrRefData.displayGraphRef._type !== "float" && independentAttrRefData.renderGraphRef._type)
        throw new Error("Expected float as type of independentAttribute parameter in diff operator")

    const diffAttrRefData = applyGeometryOperator(
        (independentAttrRef: AttributeRef, dependentAttrRef: AttributeRef) => Operators.diff(dependentAttrRef, independentAttrRef),
        independentAttrRefData,
        dependentAttrRefData,
    )

    return createCurveGeometryAttributeData(diffAttrRefData)
}

export abstract class GeometryDiffOperatorNode<out P extends DiffOperatorParameters = DiffOperatorParameters> extends GeometryAttributeNode<1, P> {
    protected override hasValidParameters(parameters: P): parameters is P & NonNullableParameters<P> {
        return parameters.independentAttribute !== null && parameters.dependentAttribute !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<P>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
    ): {evaluatedGeometryData: BuilderInlet<CurveGeometryAttributeData | null>; invalidator?: BuilderInlet<null>} {
        const {independentAttribute, dependentAttribute} = parameters
        const [evaluatedIndependentAttributeValid, evaluatedIndependentAttributeInvalid] = scope.branch(evaluator.evaluate(independentAttribute))
        const [evaluatedDependentAttributeValid, evaluatedDependentAttributeInvalid] = scope.branch(evaluator.evaluate(dependentAttribute))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(evaluatedIndependentAttributeValid, evaluatedDependentAttributeValid),
            ([independentAttributeData, dependentAttributeData]) => applyDiffOperator(independentAttributeData, dependentAttributeData),
            "applyDiffOperator",
        )

        return {
            evaluatedGeometryData: geometryAttributeData,
            invalidator: scope.phi(evaluatedIndependentAttributeInvalid, evaluatedDependentAttributeInvalid),
        }
    }
}
